import CredentialsApi from './credentialsApi.js';
import { pickFaaReview, toEndorsementFlags } from './credentialsPayload.js';
import { uploadKindOf } from './attachments.js';
import { apiKeyFor } from './endorsementTypes.js';

/**
 * endorsementsApi — endorsement and flight-review writes, on Symfony.
 *
 * The endorsement flags are a single `PUT /endorsement` with all four booleans;
 * attachments are one per endorsement, so their endpoints take the type and the
 * kind and need no asset id.
 *
 * FLIGHT REVIEW. A user registered before Symfony seeded the empty FAA row
 * has none, so `saveFlightReview` creates it (`POST /flight-review`) and
 * updates it (`PUT /flight-review/{id}`) otherwise.
 *
 * Dates go as `Y-m-d`, which is what the DTOs validate — not the legacy
 * `new Date(...).toString()` the PHP actions parsed.
 */
export { ENDORSEMENT_TYPES } from './endorsementTypes.js';

/** Save the ticked endorsements — every flag is sent, ticked or not. */
export const saveEndorsements = keys => CredentialsApi.updateEndorsement(toEndorsementFlags(keys));

/**
 * Save the FAA flight review dates.
 *
 * The legacy factory (`user.js:54`) built `date` and `expirationDate` as
 * FUNCTION expressions and never called them — `JSON.stringify` drops
 * function-valued properties, so the legacy request carried only
 * `{type, userID}` and its Save silently RESET the dates. This sends them.
 */
export const saveFlightReview = async ({ reviewId, date }) => {
	// FAA: the server derives the expiry from the date and ignores a sent one.
	const body = { date: date || null };
	if (reviewId) return CredentialsApi.updateFlightReview(reviewId, body);
	try {
		return await CredentialsApi.createFlightReview(body);
	} catch (e) {
		// 409: it was created meanwhile (another tab, the app) — update that one.
		if (e?.response?.status !== 409) throw e;
		const existing = pickFaaReview(await CredentialsApi.getFlightReviews());
		if (!existing?.id) throw e;
		return CredentialsApi.updateFlightReview(existing.id, body);
	}
};

// The asset routes take the API's type name (`tailWheel`), not our key (`tailwheel`).

/** Remove an endorsement's attachment. One per endorsement, so no asset id. */
export const deleteEndorsementAsset = (kind, typeKey) =>
	CredentialsApi.removeEndorsementAsset(apiKeyFor(typeKey), kind === 'image' ? 'image' : 'file');

/** Attach a file to an endorsement. */
export const uploadEndorsementAsset = (typeKey, file) =>
	CredentialsApi.uploadEndorsementAsset(apiKeyFor(typeKey), file, uploadKindOf(file));
